import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faEdit } from '@fortawesome/free-solid-svg-icons'
import { useEffect, useState, useRef } from 'react'
import { useOhData } from '../layouts/OhLayout'
import { useNavigate } from 'react-router-dom'

import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'

import UserDetailsSection from './UserDetailsSection'
import CancelRequest from './CancelRequest'
import OHRequestForm from './OHRequestForm'

const OHRequestPage = () => {
    const { auth, setShowLoader, createInfo } = useAuth()
    const { orgInfo, requestData } = useOhData()

    const [loading, setLoading] = useState(true)

    const navigate = useNavigate()
    const axios = useAxiosPrivate()

    const isDraftEdit = (requestData?.request_status || '').toLowerCase() === 'draft'

    const [editMode, setEditMode] = useState(false)

    const [saveDraftConfirmOpen, setSaveDraftConfirmOpen] = useState(false)
    const [showMoreBelowHint, setShowMoreBelowHint] = useState(false)
    const [showValidationTooltip, setShowValidationTooltip] = useState(false)
    const [workingHoursDropdown, setWorkingHoursDropdown] = useState('')

    const formRef = useRef(null)

    const [formData, setFormData] = useState({
        staff_details: {
            first_name: '',
            last_name: '',
            staff_name: '',
            staff_email: '',
            date_of_birth: '',
            phone_number: '',
            role: '',
            in_work: false,
            full_time: false,
            working_hours: '',
            contact_name: auth.user.contact_name,
            contact_email: auth.user.contact_email,
        },
        form_responses: {
            question1: {
                question_title: 'What support is being sought and why?',
                responses: []
            },
            question2: {
                question_title: 'What is the main nature of the employee issue?',
                responses: []
            },
            question3: {
                question_title: 'What has happened so far?',
                responses: []
            },
            question4: {
                question_title: 'What actions have been implemented?',
                responses: []
            },
            question5: {
                question_title: 'What is the desired outcome?',
                responses: []
            },
            question6: {
                question_title: 'What questions would you want us to ask the employee?',
                responses: []
            },
            question7: {
                question_title: 'Additional Information',
                responses: {
                    additional_info: {
                        label: 'Additional Information',
                        value: ''
                    }
                }
            }
        },
        declarations: {
            purpose_explained: false,
            questions_aware: false,
            abandonment: false
        },
        titleId: '13172b95-d9db-431d-957c-7f3049c24f6f',
        policyId: auth?.currentPolicy?.policy_id ?? '',
        school_name: auth.schools[0].name ?? '',
        school_address: auth.schools[0].address ?? '',
        school_postcode: auth.schools[0].postcode ?? '',
        org_id: auth.schools[0].id ?? '',
        org_urn: auth.schools[0].urn ?? '',
    })

    const [files, setFiles] = useState({
        fileList: [],
        uploading: false,
        errorMsg: false,
    })

    useEffect(() => {

        setLoading(true)
        try {
            if (requestData) {
            }
        } catch (error) {
            console.error('Error initializing OH request page:', error)
        } finally {
            setLoading(false)
        }
    }, [requestData])

    const toggleEditMode = () => {
        setEditMode(prevEditMode => !prevEditMode)
    }

    const checkHoursEntered = () => {
        if (workingHoursDropdown === 'FT') {
            return true
        } else if (workingHoursDropdown === 'PT' && formData.staff_details.working_hours) {
            return true
        } else {
            return false
        }
    }

    const isStaffDetailsComplete = () => {
        const values = formData.staff_details
        return values?.first_name?.length > 0 &&
            values?.last_name?.length > 0 &&
            values?.staff_email?.length > 0 &&
            values?.date_of_birth?.length > 0 &&
            values?.phone_number?.length > 0 &&
            values?.role?.length > 0 &&
            checkHoursEntered() &&
            (values.in_work === true || values.in_work === false)
    }

    const isPurposeComplete = () => {
        const decl = formData.declarations
        return decl.purpose_explained && decl.questions_aware && decl.abandonment
    }

    const isQuestionComplete = (questionNumber) => {

        switch (questionNumber) {
            case 1:
                return isStaffDetailsComplete()
            case 2:
            case 3:
            case 5:
                return formData?.form_responses?.[`question${questionNumber - 1}`]?.responses?.length > 0
            case 4:
                const responses = formData?.form_responses?.question3?.responses || [[], []];
                const hasMeetingsHeld = Array.isArray(responses[0]) && responses[0].length > 0;
                const hasOutcomes = Array.isArray(responses[1]) && responses[1].length > 0;

                return (hasMeetingsHeld && hasOutcomes)
            case 6:
                return formData?.form_responses?.question5?.responses?.length > 0
            case 7:
                const employeeQuestions = formData?.form_responses?.question6?.responses?.length || 0;
                return employeeQuestions > 0 && employeeQuestions <= 3;
            case 8:
                return true
            case 9:
                return isPurposeComplete()
            default:
                return false
        }

    }

    const isFormValid = () => {
        const values = formData.staff_details
        const hasStaffInfo = isStaffDetailsComplete()

        const hasDeclarations = isPurposeComplete()

        const allQuestionsComplete = isQuestionComplete(1) &&
            isQuestionComplete(2) &&
            isQuestionComplete(3) &&
            isQuestionComplete(4) &&
            isQuestionComplete(5) &&
            isQuestionComplete(6) &&
            isQuestionComplete(7) &&
            isQuestionComplete(8) &&
            isQuestionComplete(9)

        return hasStaffInfo && hasDeclarations && allQuestionsComplete
    }

    const clearFields = () => {
        setFormData({
            staff_details: {
                first_name: '',
                last_name: '',
                staff_name: '',
                staff_email: '',
                date_of_birth: '',
                phone_number: '',
                role: '',
                in_work: false,
                full_time: false,
                working_hours: '',
                contact_name: '',
                contact_email: '',
            },
            form_responses: {
                question1: {
                    question_title: 'What support is being sought and why?',
                    responses: []
                },
                question2: {
                    question_title: 'What is the main nature of the employee issue?',
                    responses: []
                },
                question3: {
                    question_title: 'What has happened so far?',
                    responses: []
                },
                question4: {
                    question_title: 'What actions have been implemented?',
                    responses: []
                },
                question5: {
                    question_title: 'What is the desired outcome?',
                    responses: []
                },
                question6: {
                    question_title: 'What questions would you want us to ask the employee?',
                    responses: []
                },
                question7: {
                    question_title: 'Additional Information',
                    responses: {
                        additional_info: {
                            label: 'Additional Information',
                            value: ''
                        }
                    }
                }
            },
            declarations: {
                purpose_explained: false,
                question_aware: false,
                abandonment: false
            },
            titleId: '13172b95-d9db-431d-957c-7f3049c24f6f',
            policyId: auth?.currentPolicy?.policy_id ?? '',
            school_name: auth.schools[0].name ?? '',
            school_address: auth.schools[0].address ?? '',
            school_postcode: auth.schools[0].postcode ?? '',
            org_id: auth.schools[0].id ?? '',
            org_urn: auth.schools[0].urn ?? '',
        })

        setFiles({
            fileList: [],
            uploading: false,
            errorMsg: false,
        })
    }

    const buildRequestPayload = () => {

        let payload = formData;

        let formattedPhoneNumber = formData.staff_details?.phone_number || ''

        if (formattedPhoneNumber) {
            if (formattedPhoneNumber.length === 11 && formattedPhoneNumber.startsWith('0')) {
                formattedPhoneNumber = formattedPhoneNumber.replace(/^0/, '')
            }

            formattedPhoneNumber = formattedPhoneNumber.replace(/\+44/g, '')
            formattedPhoneNumber = '+44' + formattedPhoneNumber
        }

        payload.staff_details.phone_number = formattedPhoneNumber
        payload.staff_details.staff_name = `${formData.staff_details.first_name} ${formData.staff_details.last_name}`

        return payload
    }

    const uploadDocuments = async (serviceRequestId) => {
        if (files.fileList.length === 0) return

        const formData = new FormData()
        files.fileList.forEach(file => {
            formData.append('files', file)
        })

        const uploadResponse = await axios.post(
            `/service-requests/oh/upload-supporting-documents/${serviceRequestId}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        )

        if (uploadResponse.status !== 200) {
            throw new Error('Failed to upload supporting documents')
        }
    }

    const submitRequest = async (e) => {
        e.preventDefault()
        setShowLoader(true)

        try {
            const payload = buildRequestPayload()

            if (isDraftEdit) {
                const updateResponse = await axios.post(`/service-requests/oh/update/${requestData.id}`, payload)

                if (updateResponse.status !== 200) {
                    console.error('Failed to update draft before submitting')
                    createInfo('error', 'Failed to submit referral, please try again')
                    return
                }

                if (files.fileList.length > 0) {
                    try {
                        await uploadDocuments(requestData.id)
                    } catch (error) {
                        console.error('Failed to upload supporting documents')
                        createInfo('error', 'Referral updated but failed to upload supporting documents')
                    }
                }

                const submitResponse = await axios.post(`/service-requests/oh/submit/${requestData.id}`)

                if (submitResponse.status !== 200) {
                    console.error('Failed to submit draft')
                    createInfo('error', 'Failed to submit referral, please try again')
                    return
                }

                clearFields()
                createInfo('success', 'Referral submitted successfully')
                navigate('/oh/dashboard')
                return
            }

            const response = await axios.post('/service-requests/oh/create', payload)

            if (response.status !== 200) {
                console.error('Failed to submit request')
                createInfo('error', 'Failed to submit request, please try again')
                return
            }

            let service_request = response.data.service_request

            if (files.fileList.length > 0) {
                try {
                    await uploadDocuments(service_request.id)
                } catch (error) {
                    console.error('Failed to upload supporting documents')
                    createInfo('error', 'Request created but failed to upload supporting documents')
                }
            }

            clearFields()
            createInfo('success', 'OH Request created successfully')
            navigate('/oh/dashboard')
        } catch (error) {
            console.error('Error:', error)
            createInfo('error', 'Failed to submit request, please try again')
        } finally {
            setShowLoader(false)
        }
    }

    const requestSaveForReview = (e) => {
        e.preventDefault()

        const normalized = (str) => (typeof str === 'string' ? str.trim() : '')

        const hasDraftContent =
            formData.declarations.purpose_explained ||
            formData.declarations.questions_aware ||
            formData.declarations.abandonment ||
            normalized(formData?.school_name) !== normalized(orgInfo?.name) ||
            normalized(formData?.school_postcode) !== normalized(orgInfo?.postcode)

        if (!hasDraftContent) {
            createInfo('error', 'Please enter at least one field before saving a draft')
            return
        }

        setSaveDraftConfirmOpen(true)
    }

    const saveForReview = async () => {
        setSaveDraftConfirmOpen(false)
        setShowLoader(true)

        try {
            const basePayload = {
                ...buildRequestPayload(),
                draft: true,
            }

            const response = isDraftEdit
                ? await axios.post(`/service-requests/oh/update/${requestData.id}`, basePayload)
                : await axios.post('/service-requests/oh/create', basePayload)

            if (response.status !== 200) {
                console.error('Failed to save draft')
                createInfo('error', 'Failed to save draft, please try again')
                return
            }

            let service_request = response.data.service_request
            const serviceRequestId = isDraftEdit ? requestData.id : service_request.id

            if (files.fileList.length > 0) {
                try {
                    await uploadDocuments(serviceRequestId)
                } catch (error) {
                    console.error('Failed to upload supporting documents')
                    createInfo('error', 'Draft saved but failed to upload supporting documents')
                }
            }

            clearFields()
            createInfo('success', 'Draft saved for review')
            navigate('/oh/dashboard')
        } catch (error) {
            console.error('Error:', error)
            createInfo('error', 'Failed to save draft, please try again')
        } finally {
            setShowLoader(false)
        }
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#02ADAD]"></div>
            </div>
        )
    }

    if (!orgInfo) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600">Error: Unable to load organization information</p>
                <button
                    onClick={() => navigate('/oh/dashboard')}
                    className="mt-4 px-4 py-2 bg-[#02ADAD] text-white rounded hover:bg-[#018f8f]"
                >
                    Return to Dashboard
                </button>
            </div>
        )
    }

    return (
        <div className="w-full max-w-4xl mx-auto bg-white rounded-lg">
            <div className="mb-4 p-4 border-b border-gray-200">
                <button
                    onClick={() => navigate('/oh/dashboard')}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Dashboard
                </button>
            </div>

            <form ref={formRef} onSubmit={submitRequest}>
                <div className="relative flex-1 pb-16">
                    <div className="px-4 py-2">
                        <OHRequestForm
                            requestData={requestData}
                            formData={formData}
                            setFormData={setFormData}
                            files={files}
                            setFiles={setFiles}
                            isDraftEdit={isDraftEdit}
                            workingHoursDropdown={workingHoursDropdown}
                            setWorkingHoursDropdown={setWorkingHoursDropdown}
                            isQuestionComplete={isQuestionComplete}
                        />
                    </div>
                </div>

                <div className="sticky bottom-0 z-20 bg-white border-t border-gray-200">
                    <UserDetailsSection
                        auth={auth}
                        orgInfo={orgInfo}
                        formData={formData}
                        setFormData={setFormData}
                        editMode={editMode}
                        toggleEditMode={toggleEditMode}
                        showMoreBelowHint={showMoreBelowHint}
                    />

                    <div className="px-4 pb-3">
                        <div className="w-full mx-auto relative">
                            {showValidationTooltip && (
                                <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-red-600 text-white text-sm px-3 py-2 rounded-md shadow-lg z-10 whitespace-nowrap">
                                    <div className="text-center">Please complete all required fields</div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-600"></div>
                                </div>
                            )}

                            {isFormValid() && (
                                <button
                                    type="submit"
                                    className="border flex items-center rounded h-[42px] w-full border-[#02ADAD] cursor-pointer hover:shadow-md transition-shadow"
                                    onClick={submitRequest}
                                >
                                    <div className="h-full flex justify-center items-center px-6 font-medium w-full text-[#02ADAD]">
                                        {isDraftEdit ? 'Submit referral' : (requestData ? 'Update Request' : 'Submit OH Request')}
                                    </div>
                                    <div className="text-white h-full flex w-[42px] justify-center items-center bg-[#02ADAD]">
                                        <FontAwesomeIcon icon={requestData ? faEdit : faPlus} />
                                    </div>
                                </button>
                            )}

                            <p className="mt-3 mb-1 text-center text-gray-400 text-sm">Don't want to submit just yet?</p>

                            <button
                                type="button"
                                className="border flex items-center rounded h-[42px] w-full border-gray-700 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={requestSaveForReview}
                            >
                                <div className="h-full flex justify-center items-center px-6 font-medium w-full text-gray-800">
                                    {isDraftEdit ? 'Save changes (Draft — does not submit)' : 'Save for review (Draft — does not submit)'}
                                </div>
                                <div className="text-white h-full flex w-[42px] justify-center items-center bg-gray-700">
                                    <FontAwesomeIcon icon={faEdit} />
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </form >

            <CancelRequest
                isOpen={saveDraftConfirmOpen}
                onClose={() => setSaveDraftConfirmOpen(false)}
                onCancel={saveForReview}
                title={isDraftEdit ? 'Save changes as draft?' : 'Save for review?'}
                message="Saving for review will NOT submit this referral. It will be saved as a draft which you can submit later. Do you want to continue?"
                confirmLabel={isDraftEdit ? 'Save Draft Changes' : 'Save as Draft'}
                confirmButtonClassName="inline-flex w-full justify-center rounded-md bg-[#02ADAD] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#02ADAD] sm:col-start-2"
            />
        </div>
    )
}

export default OHRequestPage
